// src/components/AccountSettings.tsx
import React, { useState, useEffect } from 'react';
import '../styles/AccountSettings.css';
import Button from './Button';
import {
  updateCurrentUser,
  changePassword,
  deleteAccount,
} from '../services/userService';
import { logout } from '../services/authService';
import { useNavigate } from 'react-router-dom';
import Modal from './Modal';
import { openBillingPortal } from '../services/subscriptionService';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import LoadingIndicator from './LoadingIndicator';

interface NotificationInfo {
  message: string;
  bgColor: string;
  textColor?: string;
}

interface User {
  id: number;
  username: string;
  email: string;
  contact?: string;
  is_premium: boolean;
  line_social_id?: string | null;
}

interface AccountSettingsProps {
  user: User | null;
  setUser: (user: User) => void;
  setNotification: (notification: NotificationInfo) => void;
}

const AccountSettings: React.FC<AccountSettingsProps> = ({
  user,
  setUser,
  setNotification,
}) => {
  // ───────────────────────────────── Hooks は冒頭でまとめて呼ぶ ────────────────────────────────
  const navigate = useNavigate();
  const {
    data: subscription,
    isLoading: isSubLoading,
    isError: isSubError,
  } = useSubscriptionStatus();

  // Profile 初期セット
  const [profile, setProfile] = useState({
    username: '',
    email: '',
    contact: '',
  });
  useEffect(() => {
    if (user) {
      setProfile({
        username: user.username,
        email: user.email,
        contact: user.contact ?? '',
      });
    }
  }, [user]);

  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [onConfirmAction, setOnConfirmAction] = useState<() => void>(
    () => () => {}
  );
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  // ──────────────────────────────────────────────────────────────────────────────────────────────

  // early returns: user / subscription の読み込み・エラー
  if (!user || isSubLoading) {
    return <LoadingIndicator isFullPage size={60} />;
  }
  if (isSubError) {
    return <div>会員区分の取得に失敗しました。再度リロードしてください。</div>;
  }

  // subscription がある場合のプラン名判定
  const planName = subscription ? subscription.plan.name : null;
  const isSocialLogin = Boolean(user.line_social_id);

  const membershipClass =
  planName === 'Premium'
    ? 'state-premium'
    : planName === 'Basic'
      ? 'state-basic'
      : '';

  // モーダルを開く
  const openConfirmModal = (message: string, action: () => void) => {
    setConfirmMessage(message);
    setOnConfirmAction(() => action);
    setConfirmModalOpen(true);
  };

  // プロフィール更新ハンドラ
  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };
  const confirmProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    openConfirmModal('本当にプロフィールを更新しますか？', async () => {
      setSavingProfile(true);
      try {
        const res = await updateCurrentUser(profile);
        setUser(res.data);
        setNotification({
          message: 'プロフィールが更新されました。',
          bgColor: '#28a745',
        });
      } catch {
        setNotification({
          message: 'プロフィールの更新に失敗しました。',
          bgColor: '#dc3545',
        });
      } finally {
        setSavingProfile(false);
        setConfirmModalOpen(false);
      }
    });
  };

  // パスワード変更ハンドラ
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };
  const confirmPasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    openConfirmModal('本当にパスワードを変更しますか？', async () => {
      setChangingPassword(true);
      try {
        await changePassword(passwordData);
        setNotification({
          message: 'パスワードが変更されました。',
          bgColor: '#28a745',
        });
        setPasswordData({ old_password: '', new_password: '' });
      } catch {
        setNotification({
          message: 'パスワード変更に失敗しました。',
          bgColor: '#dc3545',
        });
      } finally {
        setChangingPassword(false);
        setConfirmModalOpen(false);
      }
    });
  };

  // ログアウト
  const confirmLogout = () => {
    openConfirmModal('本当にログアウトしますか？', () => {
      logout();
      navigate('/login');
      setConfirmModalOpen(false);
    });
  };

  // 請求ポータル
  const handleOpenPortal = async () => {
    try {
      const res = await openBillingPortal();
      window.location.href = res.data.url;
    } catch {
      setNotification({
        message: '支払い情報更新ページの生成に失敗しました。',
        bgColor: '#dc3545',
        textColor: '#fff',
      });
    }
  };

  // アカウント削除
  const handleDeleteAccount = async () => {
    try {
      await deleteAccount();
      setNotification({
        message: 'アカウントを削除しました。ご利用ありがとうございました。',
        bgColor: '#28a745',
      });
      logout();
      navigate('/login');
    } catch {
      setNotification({
        message: 'アカウント削除に失敗しました。',
        bgColor: '#dc3545',
      });
    } finally {
      setConfirmModalOpen(false);
    }
  };

  return (
    <div className="account-section">
      <h2 className="section-title">アカウント管理</h2>

      <div className="membership-field">
        会員区分　{' '}
        <span className={`membership-state ${membershipClass}`}>
          {planName === 'Premium'
            ? 'Premiumプラン'
            : planName === 'Basic'
              ? 'Basicプラン'
              : '無料会員'}
        </span>
      </div>

      <h3 className="subsection-title">プロフィール情報</h3>
      <form onSubmit={confirmProfileSubmit}>
        <div className="account-field">
          <label>ユーザー名</label>
          <input
            type="text"
            name="username"
            value={profile.username}
            onChange={handleProfileChange}
            required
            placeholder="ユーザー名"
          />
        </div>
        <div className="account-field">
          <label>メール</label>
          <input
            type="email"
            name="email"
            value={profile.email}
            onChange={handleProfileChange}
            required
            placeholder="メールアドレス"
          />
        </div>
        <div className="account-field">
          <label>連絡先</label>
          <input
            type="text"
            name="contact"
            value={profile.contact}
            onChange={handleProfileChange}
            placeholder="連絡先（任意）"
          />
        </div>
        <Button type="submit" disabled={savingProfile} width="280px">
          {savingProfile ? '更新中…' : 'プロフィール更新'}
        </Button>
      </form>

      {/* パスワード変更セクション */}
      {!isSocialLogin && (
        <div className="password-section">
          <h3
            className="subsection-title clickable"
            onClick={() => setIsPasswordOpen((prev) => !prev)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            {isPasswordOpen ? '▼' : '▶︎'} パスワード変更
          </h3>
          {isPasswordOpen && (
            <form onSubmit={confirmPasswordSubmit}>
              <div className="account-field">
                <label>古いパスワード</label>
                <input
                  type="password"
                  name="old_password"
                  value={passwordData.old_password}
                  onChange={handlePasswordChange}
                  required
                  placeholder="古いパスワード"
                />
              </div>
              <div className="account-field">
                <label>新しいパスワード</label>
                <input
                  type="password"
                  name="new_password"
                  value={passwordData.new_password}
                  onChange={handlePasswordChange}
                  required
                  placeholder="新しいパスワード"
                />
              </div>
              <Button type="submit" disabled={changingPassword} width="280px">
                {changingPassword ? '変更中…' : 'パスワード変更'}
              </Button>
            </form>
          )}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '2rem',
          margin: '2rem 0',
        }}
      >
        <Button bgColor="#007bff" onClick={handleOpenPortal} width="280px">
          支払い情報管理
        </Button>
        <Button
          bgColor="#dc3545"
          textColor="#fff"
          onClick={confirmLogout}
          width="280px"
        >
          ログアウト
        </Button>
      </div>

      <div className="delete-account-section" style={{ marginTop: 16 }}>
        <p
          onClick={() =>
            openConfirmModal(
              'アカウントを完全に削除しますか？※この操作は即時実行され、有料プランは自動解約されます。',
              handleDeleteAccount
            )
          }
        >
          <span className="delete-account-button">アカウント削除</span>
        </p>
      </div>

      {confirmModalOpen && (
        <Modal isOpen onClose={() => setConfirmModalOpen(false)}>
          <h2>確認</h2>
          <p>{confirmMessage}</p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 16,
            }}
          >
            <Button
              bgColor="#6c757d"
              textColor="#fff"
              onClick={() => setConfirmModalOpen(false)}
            >
              キャンセル
            </Button>
            <Button
              bgColor="#28a745"
              textColor="#fff"
              onClick={onConfirmAction}
              style={{ marginLeft: 8 }}
            >
              実行する
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AccountSettings;
